import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { INSURANCE_FAQ_CATEGORIES } from "@/content/insurance-corpus/faq-defs";

const ADVICE_TH = [
  "ตรวจวงเงินประกันขั้นต่ำจากประกาศฉบับที่ใช้บังคับ ณ วันยื่นวีซ่าเสมอ เพราะตัวเลขของ Non-O-A และ LTR มีการปรับหลายครั้ง",
  "ซื้อกรมธรรม์จากบริษัทที่ได้รับใบอนุญาตในไทยและบันทึกในระบบ longstay.tgia.org จะช่วยให้เจ้าหน้าที่ตรวจสอบออนไลน์ได้ทันที",
  "ต่ออายุกรมธรรม์ให้คลุมเกินวันสิ้นสุดการอนุญาตให้อยู่ต่อ อย่าให้หมดอายุก่อนวันที่ขอ เพราะเป็นเหตุตีกลับที่พบบ่อยที่สุด",
  "กรอกใบคำขอเอาประกันด้วยตนเองและแถลงประวัติสุขภาพตามจริง เพื่อไม่ให้สัญญาตกเป็นโมฆียะตาม ป.พ.พ. มาตรา 865",
  "เก็บใบเสร็จตัวจริง ใบสรุปค่ารักษา และใบรับรองแพทย์ทุกครั้ง และขอฉบับภาษาอังกฤษไว้ตั้งแต่วันออกจากโรงพยาบาล",
  "ขอหนังสือปฏิเสธเคลมเป็นลายลักษณ์อักษรที่ระบุข้อกรมธรรม์ที่อ้าง ก่อนไปร้องเรียนหรือดำเนินคดี",
  "จับกรอบเวลาสองปีตาม ป.พ.พ. มาตรา 882 ไว้ในปฏิทิน อย่ารอผลพิจารณาภายในของบริษัทจนใกล้ขาดอายุความ",
  "แยกความคุ้มครองให้ชัดระหว่างประกันส่วนกลางของนิติบุคคลอาคารชุดกับประกันทรัพย์สินในห้องชุดของเจ้าของเอง",
];

const CAUTIONS_TH = [
  "ยึดตัวเลขทุนประกันจากข้อมูลเก่าหรือคำบอกเล่า แล้วพบว่าประกาศที่ใช้บังคับกำหนดวงเงินสูงกว่าในวันยื่น",
  "ซื้อประกันต่างประเทศที่ไม่ระบุชัดว่าคุ้มครองการรักษาในประเทศไทย ทำให้เอกสารไม่ผ่านการพิจารณา",
  "ปล่อยให้ตัวแทนกรอกใบคำขอแทนโดยไม่อ่าน แล้วมีข้อความเท็จเรื่องประวัติสุขภาพติดอยู่ในเอกสาร",
  "เข้าใจว่าประกัน พ.ร.บ. ครอบคลุมค่าซ่อมรถ ทั้งที่คุ้มครองความเสียหายต่อชีวิตและร่างกายเท่านั้น",
  "ขับรถโดยไม่มีใบอนุญาตขับรถที่ใช้ได้ตามกฎหมายไทย หรือมีปริมาณแอลกอฮอล์เกินกำหนด ซึ่งเข้าข้อยกเว้นของกรมธรรม์",
  "ซื้อประกันเดินทางแล้วเลื่อนวันเดินทางแต่ไม่ขอใบสลักหลังแก้ไขวันคุ้มครอง",
  "คิดว่าสิทธิประกันสังคมใช้แทนประกันเอกชนได้ทุกกรณี ทั้งที่ผูกกับโรงพยาบาลตามบัตรรับรองสิทธิและเงื่อนไขการส่งตัว",
  "ระบุชื่อผู้รับประโยชน์ไม่ตรงกับหนังสือเดินทางหรือไม่ระบุความสัมพันธ์ ทำให้พิสูจน์ตัวบุคคลตอนเคลมยาก",
];

export const Route = createFileRoute("/insurance/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/insurance-corpus");
    return { faqs: m.INSURANCE_FAQ_TH };
  },
  component: InsuranceFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ประกันภัยและประกันสุขภาพชาวต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "ประกันสุขภาพวีซ่า Non-O-A/O-X/LTR กฎหมายสัญญาประกันภัย ป.พ.พ. ม.865 และ ม.882 เอกสารเคลมและนิติกรณ์ ประกัน พ.ร.บ.รถ ประกันเดินทางเชงเกน ประกันสังคม ประกันอัคคีภัย และการร้องเรียน คปภ. 1186",
        path: "/insurance/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/insurance/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ประกันภัยและประกันสุขภาพ", url: `${site}/insurance/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/insurance/faq")) },
      ],
    };
  },
});

function InsuranceFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ประกันภัยและประกันสุขภาพ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ประกันภัยและประกันสุขภาพสำหรับชาวต่างชาติในไทย: วีซ่า เคลม รถ เดินทาง ประกันสังคม และการร้องเรียน
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวม {faqs.length} คำตอบอ้างอิง ป.พ.พ. มาตรา 861–897, พ.ร.บ.ประกันชีวิตและประกันวินาศภัย พ.ศ. 2535,
          พ.ร.บ.คุ้มครองผู้ประสบภัยจากรถ พ.ศ. 2535, ประกาศประกันสุขภาพวีซ่า Non-O-A และหลักเกณฑ์ LTR ของ BOI,
          สำนักงานประกันสังคม และสำนักงาน คปภ.
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกอ่านตามหมวด</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {INSURANCE_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/insurance/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทีมงานตรวจกรมธรรม์และชุดเอกสารเคลมให้ก่อนยื่น</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งประเภทวีซ่าที่จะยื่น บริษัทประกันที่ถืออยู่ และประเทศปลายทางที่ต้องใช้เอกสาร
            สอบถามได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อทีมงาน</a>
        </section>
        <FaqSourceNote topic="insurance" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}